import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { BarChart3, TrendingUp, Clock, FileText, Users, Calendar, Download, Filter, Search, X, CheckCircle2, FileSpreadsheet, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import { TrendChart, DesignerRankBar } from './analytics/AnalyticsCharts';

interface ProposalAnalyticDoc {
  proposalId: string;
  designerId: string;
  designerName: string;
  clientName: string;
  totalAmount: number;
  elapsedSeconds: number;
  actionType: 'export_json' | 'print_pdf';
  status?: 'draft' | 'quoted' | 'won' | 'lost';
  updatedAt: string;
}

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'catalog_manager' | 'designer';
  disabled: boolean;
  loginCount?: number;
  lastLoginAt?: string;
}

const EXCLUDED_EMAILS = [
  'tuncerdemtr@gmail.com',
  'tunc.erdem@yahoo.com',
  'tunc.erdem@eczacibasi.com.tr',
];

const isExcludedEmail = (emailStr?: string) => {
  if (!emailStr) return false;
  const lower = emailStr.toLowerCase().trim();
  return EXCLUDED_EMAILS.some(ex => lower.includes(ex.toLowerCase()));
};

export default function AnalyticsDashboard() {
  const [proposals, setProposals] = useState<ProposalAnalyticDoc[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);

  // Search & Filter state
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserModal, setSelectedUserModal] = useState<UserItem | null>(null);

  // Date Filters
  const [dateFilter, setDateFilter] = useState<'all' | 'this_month' | 'this_year' | 'custom'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Table sort state
  type SortKey = 'volume' | 'count' | 'avgTime' | 'winRate' | 'logins';
  const [sortKey, setSortKey] = useState<SortKey>('volume');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!db) return;

    // Listen to users
    const usersCol = collection(db, 'users');
    const unsubscribeUsers = onSnapshot(usersCol, (snapshot) => {
      const list: UserItem[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        const userEmail = data.email || '';
        if (isExcludedEmail(userEmail) || isExcludedEmail(data.name)) return; // Exclude test/admin accounts
        list.push({
          id: docSnap.id,
          name: data.name || '-',
          email: userEmail || '-',
          role: data.role || 'designer',
          disabled: !!data.disabled,
          loginCount: data.loginCount || 0,
          lastLoginAt: data.lastLoginAt
        });
      });
      setUsers(list);
    }, (err) => console.error("Error loading users stats:", err));

    // Listen to proposal analytics
    const analyticsCol = collection(db, 'proposal_analytics');
    const unsubscribeAnalytics = onSnapshot(analyticsCol, (snapshot) => {
      const list: ProposalAnalyticDoc[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        const designerName = data.designerName || '';
        const designerEmail = data.designerEmail || '';

        // Exclude test/admin accounts
        if (isExcludedEmail(designerEmail) || isExcludedEmail(designerName)) return;

        list.push({
          proposalId: docSnap.id,
          designerId: data.designerId || '',
          designerName: designerName || 'Bilinmeyen Kullanıcı',
          clientName: data.clientName || 'İsimsiz Müşteri',
          totalAmount: data.totalAmount || 0,
          elapsedSeconds: data.elapsedSeconds || 0,
          actionType: data.actionType || 'export_json',
          status: data.status || 'draft',
          updatedAt: data.updatedAt || new Date().toISOString()
        });
      });
      setProposals(list);
    }, (err) => console.error("Error loading proposal analytics:", err));

    return () => {
      unsubscribeUsers();
      unsubscribeAnalytics();
    };
  }, []);

  // Filter proposals by date range and excluded emails
  const filteredProposals = proposals.filter(p => {
    if (isExcludedEmail(p.designerName)) return false;

    if (!p.updatedAt) return true;
    const itemDate = new Date(p.updatedAt);
    const now = new Date();

    if (dateFilter === 'this_month') {
      return itemDate.getMonth() === now.getMonth() && itemDate.getFullYear() === now.getFullYear();
    }

    if (dateFilter === 'this_year') {
      return itemDate.getFullYear() === now.getFullYear();
    }

    if (dateFilter === 'custom') {
      if (startDate) {
        const s = new Date(startDate);
        s.setHours(0, 0, 0, 0);
        if (itemDate < s) return false;
      }
      if (endDate) {
        const e = new Date(endDate);
        e.setHours(23, 59, 59, 999);
        if (itemDate > e) return false;
      }
      return true;
    }

    return true; // 'all'
  });

  // Filter users by search term and excluded emails
  const filteredUsers = users.filter(u => {
    if (isExcludedEmail(u.email) || isExcludedEmail(u.name)) return false;
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  // Compute overall metrics
  const totalVolume = filteredProposals.reduce((acc, curr) => acc + curr.totalAmount, 0);
  const totalCount = filteredProposals.length;
  const avgAmount = totalCount > 0 ? totalVolume / totalCount : 0;
  const totalSeconds = filteredProposals.reduce((acc, curr) => acc + curr.elapsedSeconds, 0);
  const avgSeconds = totalCount > 0 ? totalSeconds / totalCount : 0;

  const wonProposals = filteredProposals.filter(p => p.status === 'won');
  const wonVolume = wonProposals.reduce((acc, curr) => acc + curr.totalAmount, 0);
  const winRate = totalCount > 0 ? (wonProposals.length / totalCount) * 100 : 0;

  const formatDuration = (totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = Math.floor(totalSecs % 60);
    return mins > 0 ? `${mins} dk ${secs} sn` : `${secs} sn`;
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sort activities by updatedAt desc
  const sortedActivities = [...filteredProposals].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  // Monthly trend (last 6 months) — independent of the quick date-filter buttons so
  // the chart always shows a usable window; excluded emails are already filtered out of `proposals`.
  const MONTH_LABELS_TR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const monthBuckets = (() => {
    const now = new Date();
    const buckets: { key: string; label: string; volume: number; count: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTH_LABELS_TR[d.getMonth()], volume: 0, count: 0 });
    }
    const byKey = new Map(buckets.map(b => [b.key, b]));
    proposals.forEach(p => {
      if (!p.updatedAt) return;
      const d = new Date(p.updatedAt);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const bucket = byKey.get(key);
      if (bucket) {
        bucket.volume += p.totalAmount;
        bucket.count += 1;
      }
    });
    return buckets;
  })();

  const volumeTrendData = monthBuckets.map(b => ({ label: b.label, value: b.volume }));
  const countTrendData = monthBuckets.map(b => ({ label: b.label, value: b.count }));

  // Per-designer aggregate stats (respects active filters), used by both the rank chart and the table.
  const designerStats = filteredUsers.map(u => {
    const userProposals = filteredProposals.filter(p => p.designerId === u.id);
    const volume = userProposals.reduce((sum, c) => sum + c.totalAmount, 0);
    const secs = userProposals.reduce((sum, c) => sum + c.elapsedSeconds, 0);
    const avgSecs = userProposals.length > 0 ? secs / userProposals.length : 0;
    const won = userProposals.filter(p => p.status === 'won').length;
    const winRate = userProposals.length > 0 ? (won / userProposals.length) * 100 : 0;
    const isActivated = (u.loginCount || 0) > 0 || !!u.lastLoginAt;
    return { user: u, count: userProposals.length, volume, avgSecs, winRate, isActivated };
  });

  const sortedDesignerStats = [...designerStats].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortKey) {
      case 'volume': return (a.volume - b.volume) * dir;
      case 'count': return (a.count - b.count) * dir;
      case 'avgTime': return (a.avgSecs - b.avgSecs) * dir;
      case 'winRate': return (a.winRate - b.winRate) * dir;
      case 'logins': return ((a.user.loginCount || 0) - (b.user.loginCount || 0)) * dir;
      default: return 0;
    }
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 text-stone-300 group-hover:text-stone-400" />;
    return sortDir === 'desc' ? <ArrowDown className="w-3 h-3 text-brand-600" /> : <ArrowUp className="w-3 h-3 text-brand-600" />;
  };

  // Export 2-Sheet Excel Report
  const handleExportAnalyticsExcel = () => {
    const designerSummary = users.map(u => {
      const userProps = filteredProposals.filter(p => p.designerId === u.id);
      const userVol = userProps.reduce((sum, c) => sum + c.totalAmount, 0);
      const userSecs = userProps.reduce((sum, c) => sum + c.elapsedSeconds, 0);
      const userAvgSecs = userProps.length > 0 ? userSecs / userProps.length : 0;
      const isActivated = (u.loginCount || 0) > 0 || !!u.lastLoginAt;

      return {
        'Tasarımcı Adı': u.name,
        'E-posta': u.email,
        'Yetki Rolü': u.role === 'admin' ? 'Yönetici' : u.role === 'catalog_manager' ? 'Katalog Yöneticisi' : 'Tasarımcı',
        'Hesap Durumu': isActivated ? 'Aktif (Şifre Belirlendi)' : 'Davet Edildi (Şifre Bekleniyor)',
        'Giriş Sayısı': u.loginCount || 0,
        'Hazırlanan Teklif Sayısı': userProps.length,
        'Toplam Ciro (TL)': userVol,
        'Ortalama Hazırlama Süresi': userProps.length > 0 ? formatDuration(userAvgSecs) : '-',
        'Son Görülme Tarihi': u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('tr-TR') : '-'
      };
    });

    const proposalDetails = filteredProposals.map(p => ({
      'Teklif ID': p.proposalId,
      'Tasarımcı': p.designerName,
      'Müşteri Adı': p.clientName,
      'İşlem Türü': p.actionType === 'print_pdf' ? 'Yazdırdı (PDF)' : 'JSON İndirdi',
      'Toplam Tutar (TL)': p.totalAmount,
      'Hazırlama Süresi': formatDuration(p.elapsedSeconds),
      'İşlem Tarihi': new Date(p.updatedAt).toLocaleString('tr-TR')
    }));

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(designerSummary);
    const ws2 = XLSX.utils.json_to_sheet(proposalDetails);

    XLSX.utils.book_append_sheet(wb, ws1, 'Tasarımcı Özetleri');
    XLSX.utils.book_append_sheet(wb, ws2, 'Tüm Teklif İşlemleri');

    XLSX.writeFile(wb, `Intema_Tasarimci_Analiz_Raporu_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Export single designer Excel report
  const handleExportSingleDesignerExcel = (user: UserItem) => {
    const userProposals = filteredProposals.filter(p => p.designerId === user.id);

    const activityRows = userProposals.map(p => ({
      'Tarih': new Date(p.updatedAt).toLocaleString('tr-TR'),
      'Müşteri Adı': p.clientName,
      'İşlem Türü': p.actionType === 'print_pdf' ? 'Yazdırdı (PDF)' : 'JSON İndirdi',
      'Tutar (TL)': p.totalAmount,
      'Süre': formatDuration(p.elapsedSeconds)
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(activityRows);
    XLSX.utils.book_append_sheet(wb, ws, `${user.name} İşlemleri`);

    XLSX.writeFile(wb, `${user.name}_Islem_Gecmisi_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      
      {/* Header & Date Controls */}
      <div className="bg-white p-6 rounded-2xl shadow-glass border border-stone-200/80 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2 text-brand-600 font-bold">
            <BarChart3 className="w-5 h-5" />
            <h1 className="text-sm uppercase tracking-wider font-extrabold text-stone-900">Analiz & Tasarımcı Performans Paneli</h1>
          </div>
          <p className="text-xs text-stone-500 mt-1">Tasarımlar, hazırlama süreleri ve teklif hacimlerinin genel performansı.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Date Filter Buttons */}
          <div className="bg-stone-100 p-1 rounded-xl flex items-center space-x-1 border border-stone-200 text-xs font-bold">
            <button
              onClick={() => setDateFilter('all')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dateFilter === 'all' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-500 hover:text-stone-800'}`}
            >
              Tüm Zamanlar
            </button>
            <button
              onClick={() => setDateFilter('this_month')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dateFilter === 'this_month' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-500 hover:text-stone-800'}`}
            >
              Bu Ay
            </button>
            <button
              onClick={() => setDateFilter('this_year')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dateFilter === 'this_year' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-500 hover:text-stone-800'}`}
            >
              Bu Yıl
            </button>
            <button
              onClick={() => setDateFilter('custom')}
              className={`px-3 py-1.5 rounded-lg transition-all ${dateFilter === 'custom' ? 'bg-white text-stone-900 shadow-xs' : 'text-stone-500 hover:text-stone-800'}`}
            >
              Tarih Seç
            </button>
          </div>

          <button
            onClick={handleExportAnalyticsExcel}
            className="px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200/80 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-all shadow-xs"
            title="Tüm performans verilerini Excel dosyası olarak indir"
          >
            <Download className="w-3.5 h-3.5 text-emerald-600" />
            <span>Excel Raporu Al</span>
          </button>
        </div>
      </div>

      {/* Custom Date Picker Bar */}
      {dateFilter === 'custom' && (
        <div className="bg-stone-50 border border-stone-200 p-4 rounded-xl flex items-center space-x-4 animate-in fade-in">
          <div className="flex items-center space-x-2 text-xs font-bold text-stone-600">
            <Filter className="w-4 h-4 text-stone-400" />
            <span>Özel Tarih Aralığı:</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            <input 
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-brand-500 font-medium"
            />
            <span className="text-stone-400 font-bold">-</span>
            <input 
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-brand-500 font-medium"
            />
          </div>
        </div>
      )}

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        
        <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-brand-50 rounded-xl text-brand-600">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">Toplam Teklif Hacmi</p>
            <p className="text-base font-extrabold text-stone-900 mt-0.5">{formatCurrency(totalVolume)}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-emerald-200/80 bg-emerald-50/20 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-700">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] text-emerald-800 font-bold uppercase tracking-wider">Gerçekleşen Satış (Onaylanan)</p>
            <p className="text-base font-extrabold text-emerald-900 mt-0.5">{formatCurrency(wonVolume)}</p>
            <p className="text-[9px] text-emerald-700 font-bold mt-0.5">{wonProposals.length} Satış (%{winRate.toFixed(0)} Dönüşüm)</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">Teklif Sayısı</p>
            <p className="text-base font-extrabold text-stone-900 mt-0.5">{totalCount} Adet</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-sky-50 rounded-xl text-sky-600">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">Ort. Teklif Tutarı</p>
            <p className="text-base font-extrabold text-stone-900 mt-0.5">{formatCurrency(avgAmount)}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-amber-50 rounded-xl text-amber-600">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">Ort. Hazırlama Süresi</p>
            <p className="text-base font-extrabold text-stone-900 mt-0.5">{formatDuration(avgSeconds)}</p>
          </div>
        </div>

      </div>

      {/* Trend & Ranking Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TrendChart
          title="Aylık Teklif Hacmi"
          subtitle="Son 6 Ay"
          data={volumeTrendData}
          formatValue={formatCurrency}
          variant="area"
        />
        <TrendChart
          title="Aylık Teklif Adedi"
          subtitle="Son 6 Ay"
          data={countTrendData}
          formatValue={(v) => `${v} adet`}
          variant="bar"
        />
        <DesignerRankBar
          items={designerStats.map(d => ({ id: d.user.id, name: d.user.name, value: d.volume }))}
          formatValue={formatCurrency}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* User stats table (Column: 8/12) */}
        <div className="lg:col-span-8 bg-white border border-stone-200/80 rounded-xl shadow-xs overflow-hidden flex flex-col">
          <div className="bg-stone-50 border-b border-stone-150 px-5 py-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-stone-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800">Tasarımcı Performans Tablosu</h3>
              <span className="bg-stone-200 text-stone-700 text-[9px] font-bold px-2 py-0.5 rounded-full">
                {filteredUsers.length} Kullanıcı
              </span>
            </div>

            {/* Search Box */}
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-stone-400" />
              <input
                type="text"
                placeholder="Tasarımcı veya E-posta ara..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg border border-stone-200 bg-white focus:outline-none focus:border-brand-500 font-medium"
              />
              {userSearch && (
                <button 
                  onClick={() => setUserSearch('')}
                  className="absolute right-2 top-2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-stone-50/50 text-[10px] font-bold text-stone-400 border-b border-stone-155 uppercase tracking-wider">
                  <th className="py-2.5 px-4">Tasarımcı & Hesap Durumu</th>
                  <th className="py-2.5 px-4 text-center cursor-pointer group select-none" onClick={() => handleSort('logins')}>
                    <span className="inline-flex items-center gap-1">Giriş <SortIcon column="logins" /></span>
                  </th>
                  <th className="py-2.5 px-4 text-center cursor-pointer group select-none" onClick={() => handleSort('count')}>
                    <span className="inline-flex items-center gap-1">Teklif <SortIcon column="count" /></span>
                  </th>
                  <th className="py-2.5 px-4 text-right cursor-pointer group select-none" onClick={() => handleSort('volume')}>
                    <span className="inline-flex items-center gap-1 justify-end">Toplam Tutar <SortIcon column="volume" /></span>
                  </th>
                  <th className="py-2.5 px-4 text-center cursor-pointer group select-none" onClick={() => handleSort('winRate')}>
                    <span className="inline-flex items-center gap-1">Kazanma Oranı <SortIcon column="winRate" /></span>
                  </th>
                  <th className="py-2.5 px-4 text-center cursor-pointer group select-none" onClick={() => handleSort('avgTime')}>
                    <span className="inline-flex items-center gap-1">Ort. Süre <SortIcon column="avgTime" /></span>
                  </th>
                  <th className="py-2.5 px-4 text-right">Detay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                {sortedDesignerStats.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-stone-400 text-xs italic">
                      Aramaya uygun tasarımcı bulunamadı.
                    </td>
                  </tr>
                ) : (
                  sortedDesignerStats.map(({ user: u, count, volume, avgSecs, winRate, isActivated }) => {
                    return (
                      <tr
                        key={u.id}
                        onClick={() => setSelectedUserModal(u)}
                        className="hover:bg-brand-50/20 cursor-pointer transition-colors group"
                        title="Tasarımcının detaylı işlem geçmişini görmek için tıklayın"
                      >
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-bold text-stone-900 group-hover:text-brand-600 transition-colors flex items-center space-x-1.5">
                              <span>{u.name}</span>
                              <span className="text-[9px] font-bold text-stone-400 uppercase bg-stone-100 px-1.5 py-0.2 rounded">
                                {u.role === 'admin' ? 'Yönetici' : u.role === 'catalog_manager' ? 'Katalog Yön.' : 'Tasarımcı'}
                              </span>
                            </div>
                            <div className="text-[10px] text-stone-400 font-normal">{u.email}</div>

                            {/* Account Status Badge */}
                            <div className="mt-1">
                              {isActivated ? (
                                <span className="inline-flex items-center space-x-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200/60">
                                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                                  <span>Aktif (Şifre Belirlendi)</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center space-x-1 text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200/60">
                                  <Clock className="w-2.5 h-2.5 text-amber-600" />
                                  <span>Davet Edildi (Şifre Bekleniyor)</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-stone-900 font-mono">{u.loginCount || 0}</td>
                        <td className="py-3 px-4 text-center font-bold text-stone-900 font-mono">{count}</td>
                        <td className="py-3 px-4 text-right font-extrabold text-stone-900">{formatCurrency(volume)}</td>
                        <td className="py-3 px-4 text-center">
                          {count > 0 ? (
                            <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                              winRate >= 50
                                ? 'text-emerald-700 bg-emerald-50 border-emerald-200/60'
                                : winRate > 0
                                ? 'text-amber-700 bg-amber-50 border-amber-200/60'
                                : 'text-stone-500 bg-stone-100 border-stone-200/60'
                            }`}>
                              %{winRate.toFixed(0)}
                            </span>
                          ) : (
                            <span className="text-stone-300 text-xs">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center font-semibold text-stone-500 font-mono text-[11px]">
                          {count > 0 ? formatDuration(avgSecs) : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="inline-flex items-center text-[10px] font-bold text-brand-600 group-hover:text-brand-700">
                            <span>İncele</span>
                            <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent logs stream (Column: 4/12) */}
        <div className="lg:col-span-4 bg-white border border-stone-200/80 rounded-xl shadow-xs overflow-hidden flex flex-col">
          <div className="bg-stone-50 border-b border-stone-150 px-5 py-4 flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-stone-500" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800">Son İşlem Akışı</h3>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-stone-100 max-h-[400px]">
            {sortedActivities.length === 0 ? (
              <div className="p-8 text-center text-stone-400 text-xs font-semibold">Seçili filtre için kayıtlı işlem bulunmuyor.</div>
            ) : (
              sortedActivities.slice(0, 10).map((act, idx) => (
                <div key={act.proposalId + idx} className="p-4 space-y-1.5 hover:bg-stone-50/50 transition-colors">
                  <div className="flex items-center justify-between text-[9px] font-bold">
                    <span className="text-stone-400 uppercase tracking-wider">{formatDate(act.updatedAt)}</span>
                    <span className={`px-1.5 py-0.5 rounded-full border text-[9px] ${
                      act.actionType === 'print_pdf' 
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                        : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    }`}>
                      {act.actionType === 'print_pdf' ? 'Yazdırdı' : 'JSON İndirdi'}
                    </span>
                  </div>
                  <div className="text-xs text-stone-855 font-bold leading-relaxed">
                    <span className="text-stone-900 font-extrabold">{act.designerName}</span>,{' '}
                    <span className="text-[#945f46]">{act.clientName}</span> için teklif hazırladı.
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-semibold text-stone-500 pt-0.5">
                    <span>Tutar: <b className="text-stone-800 font-extrabold">{formatCurrency(act.totalAmount)}</b></span>
                    <span className="flex items-center space-x-1"><Clock className="w-3 h-3 text-stone-400" /> <span>{formatDuration(act.elapsedSeconds)}</span></span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* Designer Activity History Modal */}
      {selectedUserModal && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-stone-200 overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="bg-stone-50 px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-700 font-extrabold flex items-center justify-center text-sm uppercase">
                  {selectedUserModal.name.slice(0, 2)}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-sm font-extrabold text-stone-900">{selectedUserModal.name}</h2>
                    <span className="text-[10px] font-bold text-stone-500 uppercase bg-stone-200/80 px-2 py-0.5 rounded">
                      {selectedUserModal.role === 'admin' ? 'Yönetici' : selectedUserModal.role === 'catalog_manager' ? 'Katalog Yöneticisi' : 'Tasarımcı'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-xs text-stone-500 mt-0.5">
                    <span>{selectedUserModal.email}</span>
                    <span>•</span>
                    {(selectedUserModal.loginCount || 0) > 0 || !!selectedUserModal.lastLoginAt ? (
                      <span className="text-emerald-700 font-bold flex items-center space-x-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        <span>Aktif (Şifre Belirlendi)</span>
                      </span>
                    ) : (
                      <span className="text-amber-700 font-bold flex items-center space-x-1">
                        <Clock className="w-3 h-3 text-amber-600" />
                        <span>Davet Edildi (Şifre Bekleniyor)</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => handleExportSingleDesignerExcel(selectedUserModal)}
                  className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold flex items-center space-x-1 transition-all"
                  title="Bu tasarımcının raporunu Excel olarak indir"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Excel İndir</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedUserModal(null)}
                  className="p-1.5 text-stone-400 hover:text-stone-700 hover:bg-stone-200/60 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body: Personal KPIs & Table */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              
              {/* Designer Stats Grid */}
              {(() => {
                const designerProps = filteredProposals.filter(p => p.designerId === selectedUserModal.id);
                const designerVol = designerProps.reduce((sum, c) => sum + c.totalAmount, 0);
                const designerSecs = designerProps.reduce((sum, c) => sum + c.elapsedSeconds, 0);
                const designerAvgSecs = designerProps.length > 0 ? designerSecs / designerProps.length : 0;

                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-stone-50 p-3.5 rounded-xl border border-stone-200/80">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Hazırlanan Teklif</p>
                        <p className="text-base font-extrabold text-stone-900 mt-0.5">{designerProps.length} Adet</p>
                      </div>
                      <div className="bg-stone-50 p-3.5 rounded-xl border border-stone-200/80">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Toplam Hacim</p>
                        <p className="text-base font-extrabold text-stone-900 mt-0.5">{formatCurrency(designerVol)}</p>
                      </div>
                      <div className="bg-stone-50 p-3.5 rounded-xl border border-stone-200/80">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Ort. Hazırlama Süresi</p>
                        <p className="text-base font-extrabold text-stone-900 mt-0.5">{designerProps.length > 0 ? formatDuration(designerAvgSecs) : '-'}</p>
                      </div>
                      <div className="bg-stone-50 p-3.5 rounded-xl border border-stone-200/80">
                        <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Sisteme Giriş</p>
                        <p className="text-base font-extrabold text-stone-900 mt-0.5">{selectedUserModal.loginCount || 0} Kez</p>
                      </div>
                    </div>

                    {/* Detailed Activity Table */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 flex items-center space-x-1.5">
                          <FileText className="w-4 h-4 text-brand-600" />
                          <span>Tasarımcının İşlem Geçmişi ({designerProps.length})</span>
                        </h3>
                      </div>

                      <div className="border border-stone-200 rounded-xl overflow-hidden shadow-2xs">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="bg-stone-50 text-[10px] font-bold text-stone-400 border-b border-stone-200 uppercase tracking-wider">
                              <th className="py-2.5 px-3">Tarih & Saat</th>
                              <th className="py-2.5 px-3">Müşteri Adı</th>
                              <th className="py-2.5 px-3 text-center">İşlem Türü</th>
                              <th className="py-2.5 px-3 text-right">Teklif Tutarı</th>
                              <th className="py-2.5 px-3 text-center">Hazırlama Süresi</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                            {designerProps.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="py-8 text-center text-stone-400 text-xs italic">
                                  Bu tasarımcı tarafından henüz kaydedilmiş teklif işlemi bulunmuyor.
                                </td>
                              </tr>
                            ) : (
                              [...designerProps]
                                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                                .map((p, idx) => (
                                  <tr key={p.proposalId + idx} className="hover:bg-stone-50/60 transition-colors">
                                    <td className="py-2.5 px-3 font-semibold text-stone-600">{formatDate(p.updatedAt)}</td>
                                    <td className="py-2.5 px-3 font-bold text-stone-900">{p.clientName}</td>
                                    <td className="py-2.5 px-3 text-center">
                                      <span className={`px-2 py-0.5 rounded-full border text-[9px] font-bold ${
                                        p.actionType === 'print_pdf' 
                                          ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                                          : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                      }`}>
                                        {p.actionType === 'print_pdf' ? 'PDF Yazdırdı' : 'JSON İndirdi'}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-extrabold text-stone-900">{formatCurrency(p.totalAmount)}</td>
                                    <td className="py-2.5 px-3 text-center font-mono text-stone-600">{formatDuration(p.elapsedSeconds)}</td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}

            </div>

            {/* Modal Footer */}
            <div className="bg-stone-50 px-6 py-3 border-t border-stone-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedUserModal(null)}
                className="px-4 py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded-lg text-xs font-bold transition-colors"
              >
                Kapat
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
